/**
 * DemoController - Manages the arcade attract/demo mode
 * Tracks idle time on menu and triggers auto-play
 */
export class DemoController {
  private static instance: DemoController;

  private idleTimeout: number = 60000; // 60 seconds
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private _isDemoActive: boolean = false;
  private onDemoStart?: () => void;

  private constructor() {}

  static getInstance(): DemoController {
    if (!DemoController.instance) {
      DemoController.instance = new DemoController();
    }
    return DemoController.instance;
  }

  get isDemoActive(): boolean {
    return this._isDemoActive;
  }

  /**
   * Start watching for idle state on menu. Pass a custom `timeoutMs` to
   * override the default 60s window (e.g. 20s when the menu was re-entered
   * from an attract-mode demo and we want the loop to restart faster).
   */
  startIdleWatch(onDemoStart: () => void, timeoutMs?: number): void {
    if (timeoutMs !== undefined) this.idleTimeout = timeoutMs;
    this.onDemoStart = onDemoStart;
    this._isDemoActive = false;
    this.resetIdleTimer();
  }

  /**
   * Stop watching for idle state
   */
  stopIdleWatch(): void {
    this.clearIdleTimer();
    this.onDemoStart = undefined;
  }

  /**
   * Reset the idle timer (call on any user input). An optional `timeoutMs`
   * also reconfigures the window for subsequent cycles (used to snap back
   * from the post-demo 20s window to the normal 60s window the moment a
   * real user touches anything).
   */
  resetIdleTimer(timeoutMs?: number): void {
    this.clearIdleTimer();
    if (timeoutMs !== undefined) this.idleTimeout = timeoutMs;
    this.idleTimer = setTimeout(() => {
      this.triggerDemo();
    }, this.idleTimeout);
  }

  /**
   * Mark demo as active
   */
  startDemo(): void {
    this._isDemoActive = true;
    this.clearIdleTimer();
  }

  /**
   * End demo mode
   */
  endDemo(): void {
    this._isDemoActive = false;
    this.clearIdleTimer();
  }

  private triggerDemo(): void {
    if (this.onDemoStart) {
      this.startDemo();
      this.onDemoStart();
    }
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
